import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, IconButton, Chip, Stack, Collapse, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

const formatDuration = (duration) => {
  if (!duration) return null;
  
  const msMatch = duration.match(/^(\d+\.?\d*)ms$/);
  if (msMatch) {
    const ms = parseFloat(msMatch[1]);
    if (ms >= 1000) {
      return `${(ms / 1000).toFixed(2)}s`;
    }
    return `${ms.toFixed(0)}ms`;
  }
  
  const sMatch = duration.match(/^(\d+\.?\d*)s$/);
  if (sMatch) {
    return `${parseFloat(sMatch[1]).toFixed(2)}s`;
  }
  
  return duration;
};

const MessageToolUse = ({ toolUse, message }) => {
  const [toolUseExpanded, setToolUseExpanded] = useState(true);
  
  const messageHasContent = message && (
    (message.responseContent && message.responseContent.trim().length > 50) ||
    (message.text && message.text.length > 100)
  );
  
  const isToolActive = (tool) => {
    if (tool.completed || tool.status === 'completed') return false;
    if (messageHasContent) return false;
    if (!tool.end_time) return true;
    const endTime = tool.end_time;
    const isDuration = typeof endTime === 'string' && endTime.match(/^\d+\.?\d*(ms|s)$/);
    if (isDuration) return false;
    return true;
  };
  
  const hasActiveTools = toolUse && toolUse.some(tool => isToolActive(tool));

  useEffect(() => {
    if (hasActiveTools) {
      setToolUseExpanded(true);
    }
  }, [hasActiveTools]);

  const toolsInOrder = toolUse || [];

  if (!toolUse || toolUse.length === 0) {
    return null;
  }

  const getToolStatus = (tool) => {
    const endTime = tool.end_time;
    
    if (tool.completed || tool.status === 'completed' || messageHasContent) {
      const duration = formatDuration(endTime);
      return { 
        status: 'completed', 
        label: duration ? `${duration}` : 'Done', 
        color: 'success' 
      };
    }
    
    const isDuration = endTime && typeof endTime === 'string' && endTime.match(/^\d+\.?\d*(ms|s)$/);
    
    if (isDuration) {
      const duration = formatDuration(endTime);
      return { 
        status: 'completed', 
        label: duration || 'Done', 
        color: 'success' 
      };
    }
    
    if (tool.start_time || tool.status === 'in_progress') {
      return { status: 'in_progress', label: 'Running...', color: 'info' };
    }
    
    return { status: 'calling', label: 'Starting...', color: 'warning' };
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          p: 0.75,
          borderRadius: 1.5,
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        onClick={() => setToolUseExpanded(!toolUseExpanded)}
      >
        {hasActiveTools ? (
          <CircularProgress size={14} thickness={5} sx={{ color: 'info.main' }} />
        ) : (
          <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
        )}
        <Typography
          variant="caption"
          sx={{
            color: hasActiveTools ? 'info.main' : 'success.main',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        >
          {hasActiveTools ? `Using ${toolsInOrder.length} tool${toolsInOrder.length > 1 ? 's' : ''}` : `Used ${toolsInOrder.length} tool${toolsInOrder.length > 1 ? 's' : ''}`}
        </Typography>
        {!toolUseExpanded && (
          <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
            {toolsInOrder.slice(0, 3).map((tool, idx) => {
              const toolStatus = getToolStatus(tool);
              return (
                <Chip
                  key={idx}
                  label={tool.tool_name?.replace(/_/g, ' ').replace(/tool$/i, '').trim() || 'Tool'}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    '& .MuiChip-label': { px: 0.75 },
                    textTransform: 'capitalize',
                  }}
                  color={toolStatus.color}
                  variant="outlined"
                />
              );
            })}
            {toolsInOrder.length > 3 && (
              <Chip
                label={`+${toolsInOrder.length - 3}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  '& .MuiChip-label': { px: 0.5 },
                }}
                color="default"
                variant="outlined"
              />
            )}
          </Stack>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" sx={{ p: 0.25 }}>
          {toolUseExpanded ? (
            <ExpandLessIcon sx={{ fontSize: 16 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Box>
      <Collapse in={toolUseExpanded}>
        <Paper
          elevation={0}
          sx={{
            mt: 0.5,
            p: 1.5,
            bgcolor: 'rgba(0, 0, 0, 0.02)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
          }}
        >
          <Stack spacing={1.5}>
            {toolUse.map((tool, idx) => {
              const toolStatus = getToolStatus(tool);
              const toolName = tool.tool_name?.replace(/_/g, ' ').replace(/tool$/i, '').trim() || 'Tool';
              
              return (
                <Box 
                  key={idx} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 1.5,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: toolStatus.status === 'in_progress' ? 'rgba(33, 150, 243, 0.04)' : 'transparent',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    minWidth: 24,
                    height: 24,
                  }}>
                    {toolStatus.status === 'in_progress' ? (
                      <CircularProgress size={16} thickness={5} sx={{ color: 'info.main' }} />
                    ) : toolStatus.status === 'completed' ? (
                      <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                    ) : (
                      <HourglassEmptyIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                    )}
                  </Box>
                  
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600, 
                          color: 'text.primary',
                          textTransform: 'capitalize',
                        }}
                      >
                        {toolName}
                      </Typography>
                      <Chip
                        label={toolStatus.label}
                        size="small"
                        color={toolStatus.color}
                        sx={{ 
                          height: 18,
                          fontSize: '0.65rem',
                          fontWeight: 500,
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    </Box>
                    {tool.description && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary', 
                          mt: 0.5,
                          fontSize: '0.8rem',
                          lineHeight: 1.4,
                        }}
                      >
                        {tool.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default MessageToolUse;

